import { StackScreenProps } from "@react-navigation/stack";
import {
  Button,
  Card,
  FlatActionList,
  Page,
  Stack,
  TextButton,
  TextField,
  Text,
  Divider,
} from "@towersystems/roam-common-native";
import { ManageDocketTemplateFilterRootParamList } from "../../../../types";
import { useManageDocketTemplateFilterRootContextProvider } from "../ManageDocketTemplateFilterRootScreen/hooks";
import {
  DocketFilterFormValues,
  DocketTemplateFormValues,
} from "../../components/DocketTemplateForm/types";
import {
  Controller,
  FormProvider,
  useFieldArray,
  useForm,
  useFormContext,
  UseFormReturn,
} from "react-hook-form";
import { useTranslation } from "react-i18next";
import React, { useContext } from "react";
import { Colors, View } from "react-native-ui-lib";
import { InnerFormContext } from "../ManageDocketTemplateFilterRootScreen";
import { Ionicons } from "@expo/vector-icons";
import { DocketFilterType } from "@towersystems/roam-common/lib/generated-types";
import { CategoryIdToName, DepartmentIdToName } from "../../../../components";

export interface ManageDocketTemplateFilterScreenProps
  extends StackScreenProps<
    ManageDocketTemplateFilterRootParamList,
    "ManageDocketTemplateFilterScreen"
  > {}

export const ManageDocketTemplateFilterScreen = ({
  navigation,
}: ManageDocketTemplateFilterScreenProps) => {
  const { t } = useTranslation("settings");

  const context = useManageDocketTemplateFilterRootContextProvider();
  const { index } = context;

  const filterFormContext = useContext(
    InnerFormContext
  ) as UseFormReturn<DocketFilterFormValues>;

  const {
    handleSubmit: nestedHandleSubmit,
    watch: nestedWatch,
    formState: nestedFormState,
    control: nestedControl,
  } = filterFormContext;

  const { control } = useFormContext<DocketTemplateFormValues>();

  const { append, update, remove } = useFieldArray({
    control,
    name: "filters",
  });

  React.useEffect(() => {
    navigation.setOptions({
      headerRight: () => {
        if (index === undefined) {
          return null;
        }
        return (
          <Button
            label={t("button.remove_docket_template_filter")}
            isAction
            onPress={() => handleOnRemoveFilter(index)}
            size="compact"
          />
        );
      },
    });
  }, []);

  const handleOnRemoveFilter = (index: number) => {
    if (index === undefined) {
      return;
    }
    console.log("FINDME-REMOVE ", index);
    remove(index);
    navigation.pop();
  };

  const {
    fields: nestedFields,
    append: nestedAppend,
    remove: nestedRemove,
  } = useFieldArray<DocketFilterFormValues>({
    control: nestedControl,
    name: "values",
  });

  const watchedFilterType = nestedWatch("type");

  const handleOnSubmit = async (data: DocketFilterFormValues) => {
    const isCreate = index === undefined;
    if (isCreate) {
      append(data);
    } else {
      update(index, data);
    }
    navigation.pop();
  };

  const { isDirty, isLoading, isSubmitting, isValid, isSubmitSuccessful } =
    nestedFormState;

  const disabled =
    !isDirty || isLoading || isSubmitting || !isValid || isSubmitSuccessful;

  const handleOnAddMoreValuePressed = () => {
    if (!watchedFilterType) {
      return;
    }

    if (watchedFilterType === DocketFilterType.DEPARTMENT) {
      navigation.navigate("DepartmentListScreen");
      return;
    }

    if (watchedFilterType === DocketFilterType.CATEGORY) {
      navigation.navigate("CategoryListScreen");
      return;
    }

    nestedAppend({ value: "" });
  };

  const handleOnValueRemove = (index: number) => {
    nestedRemove(index);
  };

  const footerMarkup = (
    <Button
      disabled={disabled}
      label={t("button.save_docket_template")}
      backgroundColor={Colors.action}
      color="white"
      onPress={nestedHandleSubmit(handleOnSubmit)}
    ></Button>
  );

  const valuesMarkup = watchedFilterType ? (
    <Card title={t("card.template_filter_values.title")}>
      <Stack vertical>
        {nestedFields.map((field, index) => {
          return (
            <View key={field.id}>
              <Controller
                name={`values.${index}.value`}
                control={nestedControl}
                render={({ field }) => {
                  const trailingAccessory = (
                    <Button
                      size="compact"
                      onPress={() => handleOnValueRemove(index)}
                    >
                      <Text color="destructive">
                        <Ionicons name="trash-bin" size={24} />
                      </Text>
                    </Button>
                  );

                  if (watchedFilterType === DocketFilterType.DEPARTMENT) {
                    return (
                      <View>
                        <View
                          style={{
                            justifyContent: "space-between",
                            alignItems: "center",
                            flexDirection: "row",
                          }}
                        >
                          <DepartmentIdToName
                            departmentId={field.value}
                            variant="heading"
                          />
                          {trailingAccessory}
                        </View>
                        <Divider />
                      </View>
                    );
                  }
                  if (watchedFilterType === DocketFilterType.CATEGORY) {
                    return (
                      <View>
                        <View
                          style={{
                            justifyContent: "space-between",
                            alignItems: "center",
                            flexDirection: "row",
                          }}
                        >
                          <CategoryIdToName
                            categoryId={field.value}
                            variant="heading"
                          />
                          {trailingAccessory}
                        </View>
                        <Divider />
                      </View>
                    );
                  }

                  if (watchedFilterType === DocketFilterType.TAG) {
                    return (
                      <TextField
                        value={field.value}
                        onChangeText={field.onChange}
                        placeholder="Tag Value"
                        trailingAccessory={trailingAccessory}
                      />
                    );
                  }

                  return <View />;
                }}
              />
            </View>
          );
        })}
        <Button
          isAction
          onPress={handleOnAddMoreValuePressed}
          label="Add new"
        ></Button>
      </Stack>
    </Card>
  ) : null;

  return (
    <Page title="Manage Docket Template Filter" footer={footerMarkup}>
      <Stack vertical>
        <Card title={t("card.template_filter_details.title")}>
          <FlatActionList
            items={[
              {
                title: "Filter type",
                onPress: () =>
                  navigation.navigate("DocketTemplateFilterSelectTypeScreen"),
                key: "select-filter-type",
                componentRight: () => {
                  return (
                    <TextButton
                      onPress={() =>
                        navigation.navigate(
                          "DocketTemplateFilterSelectTypeScreen"
                        )
                      }
                    >
                      {watchedFilterType || "Set Filter Type"}
                    </TextButton>
                  );
                },
              },
            ]}
          />
        </Card>
        {valuesMarkup}
      </Stack>
    </Page>
  );
};
